import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Badge } from '@/admin/components/ui/badge';
import { Button } from '@/admin/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/admin/components/ui/card';
import { cn } from '@/admin/lib/utils';
import type { AdminChatMessage } from '../../types';
import {
  compactStatusLabel,
  formatCount,
  formatDateTime,
  formatMs,
  qualityVariant,
  statusVariant,
} from '../../format';
import { CopyId } from '../trace/copy-id';

type ConversationMessageFileRef = {
  key: string;
  refID?: string;
  kind?: string;
  path?: string;
  displayURL?: string;
  authScope?: string;
  legacy: boolean;
};

export function ConversationMessagesPanel({
  messages,
  onRequestSelect,
}: {
  messages: AdminChatMessage[];
  onRequestSelect: (requestID: string) => void;
}) {
  return (
    <Card className="mx-4 lg:mx-6">
      <CardHeader>
        <CardTitle>Conversation Messages</CardTitle>
      </CardHeader>
      <CardContent>
        {messages.length === 0 ? (
          <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
            No messages matched this conversation scope.
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map(message => (
              <ConversationMessageRow
                key={message.message_id}
                message={message}
                onRequestSelect={onRequestSelect}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ConversationMessageRow({
  message,
  onRequestSelect,
}: {
  message: AdminChatMessage;
  onRequestSelect: (requestID: string) => void;
}) {
  const content = messageContent(message);
  const refs = parseFileRefs(message.file_refs_json);
  const role = messageRole(message);
  return (
    <article
      className={cn(
        'rounded-md border bg-card p-4 shadow-xs',
        role === 'user' && 'border-sky-500/30 bg-sky-500/5',
        role === 'assistant' && 'border-emerald-500/30 bg-emerald-500/5',
        role === 'tool' && 'border-amber-500/30 bg-amber-500/5',
        role === 'system' && 'border-muted-foreground/25 bg-muted/30'
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={roleVariant(role)}>{roleLabel(role)}</Badge>
          <Badge variant={statusVariant(message.status)}>
            {compactStatusLabel(message.status)}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {formatDateTime(message.created_at)}
          </span>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <CopyId value={message.message_id} label="message id" />
          <CopyContentButton value={content.value} source={content.source} />
        </div>
      </div>

      <MessageContent content={content} />

      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <MetaId label="session" value={message.session_id} />
        <MetaId label="request" value={message.request_id} />
        <MetaId label="turn" value={message.turn_id} />
        <MetaId label="trace" value={message.trace_id} />
        <span>latency {formatMs(message.latency_ms)}</span>
        <span>tokens {formatCount(message.total_tokens)}</span>
        <span>
          in/out {formatCount(message.input_tokens)}/
          {formatCount(message.output_tokens)}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <Badge variant={qualityVariant(message.content_quality)}>
          content {message.content_quality || 'unknown'}
        </Badge>
        <Badge variant={qualityVariant(message.latency_quality)}>
          latency {message.latency_quality || 'unknown'}
        </Badge>
        <Badge variant={qualityVariant(message.token_quality)}>
          tokens {message.token_quality || 'unknown'}
        </Badge>
        <Badge variant={qualityVariant(message.user_attribution_quality)}>
          user {message.user_attribution_quality || 'unknown'}
        </Badge>
      </div>

      {refs.length > 0 ? <FileRefs refs={refs} /> : null}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {message.request_id ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onRequestSelect(message.request_id || '')}
          >
            Trace request
          </Button>
        ) : null}
        {message.linked_tool_call_ids?.map(id => (
          <LinkedTraceBadge
            key={id}
            label={`tool ${shortInlineID(id)}`}
            requestID={message.request_id}
            onRequestSelect={onRequestSelect}
          />
        ))}
        {message.linked_llm_call_ids?.map(id => (
          <LinkedTraceBadge
            key={id}
            label={`LLM ${shortInlineID(id)}`}
            requestID={message.request_id}
            onRequestSelect={onRequestSelect}
          />
        ))}
      </div>
    </article>
  );
}

function MessageContent({
  content,
}: {
  content: { value: string; source: 'content' | 'snippet' | 'ref' | 'empty' };
}) {
  if (content.source === 'empty') {
    return (
      <div className="mt-3 rounded-md border border-dashed p-3 text-sm text-muted-foreground">
        Message content was not collected.
      </div>
    );
  }
  const large = content.value.length > 1200;
  const body = (
    <div className="prose prose-sm max-w-none break-words dark:prose-invert">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{content.value}</ReactMarkdown>
    </div>
  );
  if (!large) {
    return (
      <div className="mt-3">
        <ContentSourceBadge source={content.source} />
        {body}
      </div>
    );
  }
  return (
    <details className="mt-3 rounded-md border bg-background p-3" open={false}>
      <summary className="cursor-pointer text-sm font-medium">
        Large message content
      </summary>
      <div className="mt-3">
        <ContentSourceBadge source={content.source} />
        {body}
      </div>
    </details>
  );
}

function ContentSourceBadge({
  source,
}: {
  source: 'content' | 'snippet' | 'ref' | 'empty';
}) {
  if (source === 'content') return null;
  const label =
    source === 'snippet'
      ? 'snippet only'
      : source === 'ref'
        ? 'content ref only'
        : 'missing content';
  return (
    <Badge variant="outline" className="mb-2">
      {label}
    </Badge>
  );
}

function FileRefs({ refs }: { refs: ConversationMessageFileRef[] }) {
  return (
    <div className="mt-3 space-y-2 rounded-md border bg-background p-3">
      <div className="text-xs font-medium uppercase tracking-normal text-muted-foreground">
        File refs
      </div>
      {refs.map(ref => (
        <div
          key={ref.key}
          className="grid gap-2 rounded border bg-card p-2 text-xs md:grid-cols-[8rem_minmax(0,1fr)_minmax(0,1fr)_8rem_8rem]"
        >
          <span className="font-medium">
            {ref.kind || (ref.legacy ? 'legacy' : 'file')}
          </span>
          <span className="truncate font-mono">
            {ref.refID || ref.path || '-'}
          </span>
          <span className="truncate">{ref.displayURL || '-'}</span>
          <span className="truncate">{ref.authScope || '-'}</span>
          <Badge variant={ref.legacy ? 'outline' : 'secondary'}>
            {ref.legacy ? 'legacy' : 'v2'}
          </Badge>
        </div>
      ))}
    </div>
  );
}

function CopyContentButton({
  value,
  source,
}: {
  value: string;
  source: 'content' | 'snippet' | 'ref' | 'empty';
}) {
  if (!value) return null;
  const label =
    source === 'content'
      ? 'Copy content'
      : source === 'snippet'
        ? 'Copy snippet'
        : 'Copy ref';
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={() => void navigator.clipboard?.writeText(value)}
    >
      {label}
    </Button>
  );
}

function MetaId({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <span className="inline-flex items-center gap-1">
      {label} <CopyId value={value} label={`${label} id`} />
    </span>
  );
}

function LinkedTraceBadge({
  label,
  requestID,
  onRequestSelect,
}: {
  label: string;
  requestID?: string;
  onRequestSelect: (requestID: string) => void;
}) {
  if (!requestID) {
    return <Badge variant="outline">{label}</Badge>;
  }
  return (
    <button type="button" onClick={() => onRequestSelect(requestID)}>
      <Badge variant="outline" className="cursor-pointer">
        {label}
      </Badge>
    </button>
  );
}

function messageContent(message: AdminChatMessage): {
  value: string;
  source: 'content' | 'snippet' | 'ref' | 'empty';
} {
  if (message.content?.trim()) {
    return { value: message.content.trim(), source: 'content' };
  }
  if (message.snippet?.trim()) {
    return { value: message.snippet.trim(), source: 'snippet' };
  }
  if (message.content_ref?.trim()) {
    return { value: message.content_ref.trim(), source: 'ref' };
  }
  return { value: '', source: 'empty' };
}

function messageRole(message: AdminChatMessage) {
  const role = message.role.trim().toLowerCase();
  if (role === 'user') return 'user';
  if (role === 'assistant' || role === 'agent') return 'assistant';
  if (role === 'tool' || message.message_type === 'tool') return 'tool';
  if (role === 'system' || role === 'lifecycle') return 'system';
  return 'other';
}

function roleLabel(role: ReturnType<typeof messageRole>) {
  switch (role) {
    case 'assistant':
      return 'assistant';
    case 'tool':
      return 'tool';
    case 'system':
      return 'system';
    case 'other':
      return 'other';
    default:
      return 'user';
  }
}

function roleVariant(
  role: ReturnType<typeof messageRole>
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (role) {
    case 'user':
    case 'assistant':
      return 'secondary';
    case 'tool':
      return 'outline';
    case 'system':
    case 'other':
    default:
      return 'outline';
  }
}

function parseFileRefs(value?: string | null): ConversationMessageFileRef[] {
  if (!value) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return [
      {
        key: 'legacy:0',
        path: value,
        legacy: true,
      },
    ];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.flatMap((item, index) => {
    if (typeof item !== 'object' || item === null) return [];
    const ref = item as Record<string, unknown>;
    return {
      key: `${stringValue(ref.ref_id) || stringValue(ref.refId) || index}:${index}`,
      refID: stringValue(ref.ref_id) ?? stringValue(ref.refId),
      kind: stringValue(ref.kind),
      path: stringValue(ref.path),
      displayURL:
        stringValue(ref.display_url) ??
        stringValue(ref.displayURL) ??
        stringValue(ref.displayUrl) ??
        stringValue(ref.download_url) ??
        stringValue(ref.downloadURL) ??
        stringValue(ref.downloadUrl),
      authScope:
        stringValue(ref.auth_scope) ??
        stringValue(ref.authScope) ??
        stringValue(ref.source),
      legacy: !stringValue(ref.ref_id) && !stringValue(ref.refId),
    };
  });
}

function stringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function shortInlineID(value: string) {
  if (value.length <= 18) return value;
  return `${value.slice(0, 8)}...${value.slice(-6)}`;
}
